/*
  CK Store — motion layer
  - Lenis smooth scroll (mouse / trackpad; phones keep their native scroll)
  - GSAP + ScrollTrigger: reading-progress line, image parallax, soft image reveals,
    staggered cards, word-by-word headings, magnetic buttons
  Settings: Theme settings › CK — Motion. Nothing runs for visitors who ask for reduced
  motion, or in the theme editor. The theme's own section animations stay as they are;
  this layer only animates what they don't (images, custom sections, footer).
*/
(function () {
  'use strict';
  var cfg = window.CKMotion || {};
  var html = document.documentElement;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce || window.Shopify && window.Shopify.designMode) { html.classList.remove('ck-motion'); return; }

  var hasGsap = !!(cfg.animations && window.gsap && window.ScrollTrigger);
  var lenis = null;
  var finePointer = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  // Panels that scroll on their own (drawers, popups, the bundle builder…)
  var PREVENT = [
    '[data-lenis-prevent]', '[data-scroll-lock-ignore]', '.ckb-sheet', '.ck-toast',
    '.drawer-menu', '.quick-cart', '.quick-view-modal', '.modal', '.popup', '.quick-search',
    '.filter-drawer', '.store-availability-drawer', '.mobile-filters', '.predictive-search',
    '[role="dialog"]', 'select', 'textarea'
  ].join(',');

  function headerOffset() {
    var cs = getComputedStyle(html);
    return (parseFloat(cs.getPropertyValue('--height-header')) || 0) + 12;
  }

  /* ---------- Lenis ---------- */
  function initLenis() {
    // phones / tablets keep their native scroll: no Lenis loop running for nothing
    if (!cfg.smooth || !finePointer || typeof window.Lenis !== 'function') return;
    lenis = new window.Lenis({
      lerp: 0.085,
      smoothWheel: true,
      wheelMultiplier: 1,
      allowNestedScroll: true,
      stopInertiaOnNavigate: true,
      anchors: { offset: -headerOffset() },
      prevent: function (node) { return !!(node && node.closest && node.closest(PREVENT)); }
    });
    window.ckLenis = lenis;

    if (hasGsap) {
      lenis.on('scroll', window.ScrollTrigger.update);
      window.gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
      window.gsap.ticker.lagSmoothing(0);
    } else {
      var raf = function (time) { lenis.raf(time); requestAnimationFrame(raf); };
      requestAnimationFrame(raf);
    }

    // Pause while the theme locks the page (menus, popups, quick view, bundle builder)
    var body = document.body;
    var locked = false;
    function check() {
      var now = html.classList.contains('ckb-lock') ||
        body.style.overflow === 'hidden' || html.style.overflow === 'hidden' ||
        body.style.position === 'fixed';
      if (now === locked) return;
      locked = now;
      if (locked) lenis.stop(); else lenis.start();
    }
    if (window.MutationObserver) {
      var mo = new MutationObserver(check);
      mo.observe(body, { attributes: true, attributeFilter: ['style', 'class'] });
      mo.observe(html, { attributes: true, attributeFilter: ['style', 'class'] });
    }
  }

  /* ---------- helpers ---------- */
  function all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  // CK Store: true when the element is already on screen when the page opens.
  // ScrollTrigger's clamp() puts the start of those at scroll 0, and at scroll 0 it never
  // fires — on short pages (e.g. /collections on a phone) the cards stayed invisible until
  // the visitor scrolled. Those are now animated straight away instead.
  function inView(el) {
    var r = el.getBoundingClientRect();
    return r.top < (window.innerHeight || html.clientHeight) && r.bottom > 0;
  }

  // Wrap each word in a mask so it can slide up (Arabic words stay whole, letters keep joining)
  function splitWords(el) {
    if (el.dataset.ckSplit) return all('.ck-word__in', el);
    el.dataset.ckSplit = '1';
    var label = el.textContent.replace(/\s+/g, ' ').trim();
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(function (node) {
      var parts = node.nodeValue.split(/(\s+)/);
      var frag = document.createDocumentFragment();
      parts.forEach(function (p) {
        if (!p) return;
        if (/^\s+$/.test(p)) { frag.appendChild(document.createTextNode(' ')); return; }
        var outer = document.createElement('span');
        outer.className = 'ck-word';
        outer.setAttribute('aria-hidden', 'true');
        var inner = document.createElement('span');
        inner.className = 'ck-word__in';
        inner.textContent = p;
        outer.appendChild(inner);
        frag.appendChild(outer);
      });
      node.parentNode.replaceChild(frag, node);
    });
    el.setAttribute('aria-label', label);
    return all('.ck-word__in', el);
  }

  /* ---------- GSAP ---------- */
  function initAnimations() {
    var gsap = window.gsap;
    var ST = window.ScrollTrigger;
    gsap.registerPlugin(ST);
    var rtl = html.getAttribute('dir') === 'rtl';

    // 1. Reading-progress line under the header
    var bar = document.createElement('div');
    bar.className = 'ck-progress';
    bar.setAttribute('aria-hidden', 'true');
    document.body.appendChild(bar);
    gsap.set(bar, { scaleX: 0, transformOrigin: rtl ? '100% 50%' : '0% 50%' });
    gsap.to(bar, {
      scaleX: 1,
      ease: 'none',
      scrollTrigger: { trigger: document.body, start: 'top top', end: 'bottom bottom', scrub: 0.3 }
    });

    // 2. Parallax on large section images
    // CK Store: desktop only — on phones the scroll-linked image scaling made scrolling heavy
    if (finePointer) all([
      '.image-hero__image-container',
      '.image-with-text-split__image-container',
      '.image-with-text__image-wrapper',
      '.ck-parallax'
    ].join(',')).forEach(function (box) {
      var img = box.querySelector('img');
      if (!img || box.closest('.product')) return;
      box.classList.add('ck-parallax-box');
      gsap.fromTo(img, { yPercent: -7, scale: 1.16 }, {
        yPercent: 7,
        scale: 1.16,
        ease: 'none',
        scrollTrigger: { trigger: box, start: 'top bottom', end: 'bottom top', scrub: true }
      });
    });

    // 3. Soft "curtain" reveal on split / side images (desktop only, same reason)
    if (finePointer) all('.image-with-text-split__image-container, .image-with-text__image-wrapper').forEach(function (box) {
      gsap.fromTo(box,
        { clipPath: 'inset(9% 7% 9% 7% round 18px)' },
        {
          clipPath: 'inset(0% 0% 0% 0% round 0px)',
          duration: 1.4,
          ease: 'expo.out',
          scrollTrigger: { trigger: box, start: 'clamp(top 85%)', once: true }
        });
    });

    // 4. Headings of the custom sections: word by word
    all('.ck-collections__heading, .ck-social__heading, [data-ck-split]').forEach(function (h) {
      var words = splitWords(h);
      if (!words.length) return;
      gsap.set(words, { yPercent: 110 });
      var wordsAnim = { yPercent: 0, duration: 1.05, ease: 'expo.out', stagger: 0.07 };
      if (!inView(h)) wordsAnim.scrollTrigger = { trigger: h, start: 'clamp(top 88%)', once: true };
      gsap.to(words, wordsAnim);
    });
    all('.ck-collections__sub, .ck-social__sub').forEach(function (el) {
      var subAnim = { y: 18, autoAlpha: 0, duration: 0.9, ease: 'power3.out', delay: 0.15 };
      if (!inView(el)) subAnim.scrollTrigger = { trigger: el, start: 'clamp(top 90%)', once: true };
      gsap.from(el, subAnim);
    });

    // 5. Cards: rise in, one after the other
    function batch(sel) {
      var items = all(sel);
      if (!items.length) return;
      var now = items.filter(inView);
      var later = items.filter(function (el) { return now.indexOf(el) === -1; });
      gsap.set(items, { y: 46, autoAlpha: 0 });
      // already on screen: rise in right away (no scroll needed)
      if (now.length) gsap.to(now, { y: 0, autoAlpha: 1, duration: 1, ease: 'expo.out', stagger: 0.09, delay: 0.05 });
      if (!later.length) return;
      ST.batch(later, {
        start: 'clamp(top 92%)',
        once: true,
        onEnter: function (els) {
          gsap.to(els, { y: 0, autoAlpha: 1, duration: 1, ease: 'expo.out', stagger: 0.09, overwrite: true });
        }
      });
    }
    batch('.ck-col-card');
    batch('.ck-social__card');
    batch('.footer__group');

    // 6. Cart page: offer box and lines settle in
    var offer = document.querySelector('.ck-offer-cart');
    if (offer) {
      gsap.from(offer, { y: 18, autoAlpha: 0, duration: 0.9, ease: 'expo.out', delay: 0.1 });
      var fill = offer.querySelector('.ck-offer-track__fill');
      if (fill) gsap.from(fill, { width: 0, duration: 1.4, ease: 'expo.out', delay: 0.35 });
      gsap.from(all('.ck-offer-track__step', offer), { scale: 0.6, autoAlpha: 0, duration: 0.6, ease: 'back.out(2)', stagger: 0.12, delay: 0.4 });
    }
    var lines = all('.cart__form-items > .cart__form-item');
    if (lines.length) gsap.from(lines, { y: 22, autoAlpha: 0, duration: 0.8, ease: 'power3.out', stagger: 0.07, delay: 0.2, clearProps: 'transform,opacity,visibility' });

    // 7. Magnetic primary buttons (mouse only)
    if (finePointer) {
      all('.section-blocks .btn, .ck-offer-cart__btn--primary, .ckb-box > .ckb-btn--primary, .ck-social__btn').forEach(function (btn) {
        var x = gsap.quickTo(btn, 'x', { duration: 0.5, ease: 'power3.out' });
        var y = gsap.quickTo(btn, 'y', { duration: 0.5, ease: 'power3.out' });
        btn.addEventListener('mousemove', function (e) {
          var r = btn.getBoundingClientRect();
          x((e.clientX - r.left - r.width / 2) * 0.22);
          y((e.clientY - r.top - r.height / 2) * 0.32);
        });
        btn.addEventListener('mouseleave', function () { x(0); y(0); });
      });
    }

    // Images and fonts change the page height: measure again once everything is in
    window.addEventListener('load', function () { ST.refresh(); });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { ST.refresh(); });
  }

  function init() {
    try {
      initLenis();
      if (hasGsap) initAnimations();
      if (hasGsap) html.classList.add('ck-motion-on');
      else html.classList.remove('ck-motion');
    } catch (e) {
      html.classList.remove('ck-motion');
      if (window.console) console.warn('[ck-motion]', e);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
